import { useState } from 'react';

/**
 * Reusable card on the Reports page. Renders a title, description, an
 * optional block of filter controls (children), and a Generate button
 * that surfaces loading + error state inline.
 *
 * A second, outline-styled action can be added via `secondaryLabel` +
 * `onSecondary` (e.g. a "Download CSV" next to a filled "Download PDF").
 * Each button tracks its own busy/error state so one can run while the
 * other is idle.
 *
 * Props:
 *   title          — string, big card heading
 *   description    — string, one-line explanation of what the report contains
 *   buttonLabel    — string, action label on the primary (filled) button
 *   onGenerate     — async function invoked when the primary button is clicked
 *   secondaryLabel — string, action label on the optional outline button
 *   onSecondary    — async function invoked when the secondary button is clicked
 *   children       — filter controls (date pickers, dropdowns) rendered inline
 */
export default function ReportCard({
  title,
  description,
  buttonLabel = 'Download',
  onGenerate,
  secondaryLabel,
  onSecondary,
  children,
}) {
  const [busy, setBusy] = useState(false);
  const [secondaryBusy, setSecondaryBusy] = useState(false);
  const [error, setError] = useState(null);

  function runner(action, setFlag) {
    return async () => {
      if (busy || secondaryBusy) return;
      setError(null);
      setFlag(true);
      try {
        await action();
      } catch (e) {
        setError(e?.message || 'Report generation failed.');
      } finally {
        setFlag(false);
      }
    };
  }

  const hasSecondary = secondaryLabel && onSecondary;

  return (
    <section className="report-card">
      <h3 className="report-card__title">{title}</h3>
      <p className="report-card__desc">{description}</p>
      {children && <div className="report-card__options">{children}</div>}
      <div className="report-card__footer">
        <div className="report-card__buttons">
          {hasSecondary && (
            <button
              type="button"
              className="report-card__button report-card__button--outline"
              onClick={runner(onSecondary, setSecondaryBusy)}
              disabled={busy || secondaryBusy}
            >
              {secondaryBusy ? (
                <>
                  <span className="btn-spinner" aria-hidden="true" />
                  Generating…
                </>
              ) : (
                secondaryLabel
              )}
            </button>
          )}
          <button
            type="button"
            className="report-card__button"
            onClick={runner(onGenerate, setBusy)}
            disabled={busy || secondaryBusy}
          >
            {busy ? (
              <>
                <span className="btn-spinner" aria-hidden="true" />
                Generating…
              </>
            ) : (
              buttonLabel
            )}
          </button>
        </div>
        {error && <div className="report-card__error">{error}</div>}
      </div>
    </section>
  );
}
